import { Button } from '@src/shared/ui/Button';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@src/shared/ui/Dialog';
import PropTypes from 'prop-types';
import type {
  ComponentPropsWithoutRef,
  PropsWithChildren,
  ReactNode,
} from 'react';

const noop = () => {};

type NotificationDialogButtonProps = Omit<
  ComponentPropsWithoutRef<typeof Button>,
  'onClick' | 'children'
>;

type NotificationDialogSlotProps = {
  cancelButton?: NotificationDialogButtonProps & {
    show?: boolean;
    label?: ReactNode;
  };
  confirmButton?: NotificationDialogButtonProps & {
    show?: boolean;
    label?: ReactNode;
  };
};

type NotificationDialogProps = PropsWithChildren<
  Omit<ComponentPropsWithoutRef<typeof Dialog>, 'open'> & {
    open?: boolean;
    title?: ReactNode;
    onConfirm?: () => void;
    onClose?: () => void;
    slotProps?: NotificationDialogSlotProps;
  }
>;

const styles = {
  root: {
    '& .MuiDialog-paper': {
      width: '500px',
      borderRadius: 3,
      maxHeight: 435,
    },
  },
  title: {
    paddingTop: 2.5,
    textAlign: 'center',
  },
  content: {
    fontSize: 16,
    lineHeight: 1.5,
    textAlign: 'center',
  },
  actions: {
    paddingBottom: 2.5,
    justifyContent: 'center',
  },
};

function NotificationDialog(props: NotificationDialogProps) {
  const {
    open = false,
    title = 'Notification',
    children,
    onConfirm = noop,
    onClose = noop,
    slotProps = {},
    ...restProps
  } = props;

  const { cancelButton: cancelButtonProps, confirmButton: confirmButtonProps } =
    slotProps || {};

  const {
    show: showCancelButton = true,
    label: cancelButtonLabel = 'No',
    ...restCancelButtonProps
  } = cancelButtonProps || {};
  const {
    show: showConfirmButton = true,
    label: confirmButtonLabel = 'Yes',
    ...restConfirmButtonProps
  } = confirmButtonProps || {};

  return (
    <Dialog maxWidth="sm" sx={styles.root} open={open} {...restProps}>
      <DialogTitle sx={styles.title}>{title}</DialogTitle>
      <DialogContent sx={styles.content}>{children}</DialogContent>

      <DialogActions sx={styles.actions}>
        {showCancelButton ? (
          <Button
            variant="outlined"
            {...restCancelButtonProps}
            onClick={onClose}
          >
            {cancelButtonLabel}
          </Button>
        ) : null}

        {showConfirmButton ? (
          <Button
            variant="contained"
            {...restConfirmButtonProps}
            onClick={onConfirm}
          >
            {confirmButtonLabel}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}

NotificationDialog.displayName = 'NotificationDialog';
NotificationDialog.propTypes = {
  open: PropTypes.bool,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  onConfirm: PropTypes.func,
  onClose: PropTypes.func,
  slotProps: PropTypes.object,
};
export default NotificationDialog;
